import React, { useEffect, useRef } from 'react';

/**
 * [MOTION-C 2026-08-20] An SVG line that draws itself in, left to right, when it first
 * appears or when its data changes. Used for the report charts a manager opens (Process
 * Analysis), where a line arriving instantly reads as a static picture and a line that draws
 * reads as the figures being plotted.
 *
 * WHY MEASURED, NOT `pathLength="1"`. The dash trick needs the path's length. Declaring
 * `pathLength="1"` and using `stroke-dasharray: 1` is tidier, but if anything fails to honour
 * it the dash pattern is taken in user units and a 500-unit line renders as a dotted mess —
 * a broken chart. `getTotalLength()` is measured from the real element, and when it is
 * unavailable this component simply renders a normal, complete line.
 *
 * SAFE WITH THE DASHED CONVENTIONS ON THESE CHARTS. Several series use `strokeDasharray` to
 * MEAN something (a partial/running month is dashed). This component takes over
 * dasharray/dashoffset for the duration of the draw and then REMOVES its inline values, so a
 * caller's own dash styling is left intact afterwards. Only pass solid lines to it.
 *
 * Re-renders do not restart the draw: the effect is keyed on `d`, and hovering a chart
 * changes other attributes, not the path data.
 */
interface Props extends React.SVGProps<SVGPathElement> {
  d: string;
  /** Draw duration. Long enough to read as drawing, short enough not to delay the reading. */
  drawMs?: number;
}

const prefersReducedMotion = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export const DrawnPath: React.FC<Props> = ({ d, drawMs = 620, ...rest }) => {
  const ref = useRef<SVGPathElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || typeof el.getTotalLength !== 'function') return undefined;

    let len = 0;
    try { len = el.getTotalLength(); } catch { return undefined; }
    if (!Number.isFinite(len) || len <= 0) return undefined;

    el.style.transition = 'none';
    el.style.strokeDasharray = String(len);
    el.style.strokeDashoffset = String(len);
    // Force the browser to take the from-state before the transition is applied, or the two
    // style writes collapse into one frame and nothing animates.
    void el.getBoundingClientRect();
    el.style.transition = `stroke-dashoffset ${drawMs}ms cubic-bezier(0.16, 1, 0.3, 1)`;
    el.style.strokeDashoffset = '0';

    // Clear the inline dash properties once the draw is done so the path goes back to being
    // styled purely by its own attributes (this is what keeps a caller's meaningful
    // strokeDasharray working after the animation).
    const t = window.setTimeout(() => {
      el.style.transition = '';
      el.style.strokeDasharray = '';
      el.style.strokeDashoffset = '';
    }, drawMs + 80);
    return () => window.clearTimeout(t);
  }, [d, drawMs]);

  return <path ref={ref} d={d} {...rest} />;
};

export default DrawnPath;
